import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { SavedSearchDialog } from "./save-search-dialog";
import { styles } from "./style";
import { Lead } from "model/lead";
import { RootState } from "redux/store";
import { getActiveSavedSearchesForLead } from "redux/selector";
import { fetchSavedSearchResultTotal } from "redux/slice/saved-search-result-totals";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { SavedSearchCard } from "./saved-search-card";
import { User } from "model/user";
import { LoadingIndicator } from "component/shared/loading-indicator";
import { SavedSearch } from "model/saved-search";
import * as env from "shared/env";

const mapStateToProps = (state: RootState, ownProps: any) => {
	return {
		savedSearches: getActiveSavedSearchesForLead(state, ownProps.lead.id),
		loading: state.savedSearches.loading && Object.keys(state.savedSearches.byId).length < 1,
	}
}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	fetchSavedSearchResultTotal,
}, dispatch);

interface Props extends 
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles>
{
	user: User;
	lead: Lead;
}

interface State {
	dialogOpen: boolean;
	savedSearch?: SavedSearch;
}

class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = {
			dialogOpen: false,
		};
	}

	public componentDidMount() {
		this.fetchTotalsForSavedSearches(this.props.savedSearches);
	}

	public componentDidUpdate(prevProps: Props) {
		const prevIds = new Set(prevProps.savedSearches.map((savedSearch) => savedSearch.id));
		const newSavedSearches = this.props.savedSearches.filter(
			(savedSearch) => !prevIds.has(savedSearch.id)
		);
		if (newSavedSearches.length > 0) {
			this.fetchTotalsForSavedSearches(newSavedSearches);
		}
	}

	private fetchTotalsForSavedSearches(savedSearches: SavedSearch[]) {
		const { user } = this.props;
		if (!user.permissions.leadSaveSearch) {
			return;
		}
		savedSearches.forEach((savedSearch) => {
			this.props.fetchSavedSearchResultTotal({ savedSearchId: savedSearch.id });
		});
	}

	public render() {
		const { user, lead, savedSearches, loading } = this.props;
		const { dialogOpen, savedSearch } = this.state;
		const count = savedSearches.length;
		return (
			<>
				{dialogOpen && (
					<SavedSearchDialog
						user={user}
						lead={lead}
						open={this.state.dialogOpen}
						onClose={() => this.setState({
							dialogOpen: false,
							savedSearch: undefined,
						})}
						savedSearch={savedSearch}
					/>
				)}
				
				<Mui.Grid container direction="column" spacing={2}>
					{loading ? (
						<div style={{height: "75vh"}}>
							<LoadingIndicator />
						</div>
					) : (
						<>
							<Mui.Grid item>
								<Mui.Grid container 
									justifyContent="space-between" 
									alignItems="center" 
								>
									<Mui.Grid item>
										{count === 1 &&
											<Mui.Typography>1 Saved Search</Mui.Typography>
										}
										{count > 1 &&
											<Mui.Typography>{count} Saved Searches</Mui.Typography>
										}
									</Mui.Grid>
									<Mui.Grid item>
										<Mui.Button
											variant="contained"
											color={"primary"}
											onClick={() => this.setState({dialogOpen: true})}
										>
											<Mui.Typography noWrap>Add New</Mui.Typography>
										</Mui.Button>
									</Mui.Grid>
								</Mui.Grid>
							</Mui.Grid>
							{count === 0 &&
								<Mui.Grid item>
									<Mui.Grid
										container
										direction="column"
										alignItems="center"
										justifyContent="center"
									>
										<Mui.Grid item xs={9}>
											<Mui.Typography align="center" >
												This lead has no saved searches yet. Add a saved search to their Property Organizer account by clicking the ADD NEW button.
											</Mui.Typography>
										</Mui.Grid>
									</Mui.Grid>
								</Mui.Grid>
							}
							<Mui.Grid item>
								{savedSearches.map((savedSearch, index) => (
									<SavedSearchCard 
										key={index} 
										lead={lead} 
										savedSearch={savedSearch} 
										openEditDialogue={() => this.setState({savedSearch, dialogOpen: true})}
									/>
								))}
							</Mui.Grid>
						</>
					)}
				</Mui.Grid>
			</>
		);
	}
}

export const SavedSearches = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
